import React, { useState } from "react";
import i18n from "../lib/i18n";
import { number, string, oneOfType, func, bool } from "prop-types";
import Dropzone from "react-dropzone";

import { classSet } from "../lib/helpers";

const AttachmentDropzone = (props) => {
  const MAX_SIZE = props.maxSize * 1000 * 1000;
  const MAX_FILES = 5;
  const isStandAlone = props.standAlone || false;

  const [uploadState, setUploadState] = useState({ uploading: false });
  const [isDragOver, setIsDragOver] = useState(false);
  const [notice, setNotice] = useState(null);
  const [counter, setCounter] = useState(0);

  const uploadAttachment = async (file) => {
    const data = new FormData();
    data.append("authenticity_token", window.authToken());
    data.append("file", file);
    data.append("resource_type", props.resourceType);
    data.append("resource_id", props.resourceId);
    if (props.context) data.append("context", props.context);

    const response = await fetch("/attachments", {
      method: "POST",
      body: data,
      credentials: "same-origin",
    });

    if (!response.ok) {
      let errors = [i18n.t("js.components.attachment_dropzone.error")];
      try {
        const body = await response.json();
        errors = body.errors;
      } catch (e) {
        console.error(e);
      }
      return { success: false, errors, file: file.name };
    }

    let converted = false;
    try {
      const body = await response.json();
      converted = body.converted;
    } catch (e) {
      console.error(e);
    }
    return { success: true, file: file.name, converted };
  };

  const handleDrop = async (acceptedFiles, rejectedFiles) => {
    setIsDragOver(false);

    const validation = validateFiles(
      acceptedFiles,
      rejectedFiles,
      MAX_FILES,
      MAX_SIZE
    );

    if (!validation.valid) {
      if (isStandAlone) {
        dispatchStandAloneUploadFailedEvent(validation.notice);
      }
      setNotice(validation.notice);
      return;
    }

    setUploadState({
      uploading: true,
      total: acceptedFiles.length,
      completed: 0,
    });
    setNotice(null);

    // Upload files sequentially to reduce load on backend
    let results = [];
    for (const file of acceptedFiles) {
      results.push(await uploadAttachment(file));
      setUploadState((state) =>
        Object.assign({}, state, { completed: state.completed + 1 })
      );
    }
    setUploadState({ uploading: false });

    const successCount = results.filter((item) => item.success).length;
    const failureCount = results.filter((item) => !item.success).length;
    const uploadNotice = getUploadNotice(results);

    setNotice(uploadNotice);

    if (isStandAlone) {
      if (failureCount > 0) {
        dispatchStandAloneUploadFailedEvent(uploadNotice);
      } else {
        setCounter(counter + 1);
        dispatchStandAloneUploadCompleteEvent(uploadNotice);
      }
    } else if (successCount > 0) {
      props.onUploadComplete();
    }
  };

  return (
    <React.Fragment>
      {!isStandAlone && notice && (
        <div className={`alert alert-${notice.alertType}`} role="alert">
          <div className="alert-content">
            <ul>
              {notice.messages.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Dropzone
        maxSize={MAX_SIZE}
        multiple={true}
        onDrop={handleDrop}
        onDragEnter={() => setIsDragOver(true)}
        onDragLeave={() => setIsDragOver(false)}
        disabled={uploadState.uploading}
      >
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps({
              className: classSet("dropzone", {
                dragover: isDragOver,
              }),
              style: { marginBottom: "20px" },
            })}
          >
            <input {...getInputProps()} />
            <p>{getDropzoneText(uploadState, MAX_SIZE)}</p>
            {getAllowedExtensionsText(props.allowedExtensions)}
            <span>
              <i className="fa fa-plus" style={{ fontSize: "24px" }}></i>
            </span>
          </div>
        )}
      </Dropzone>
    </React.Fragment>
  );
};

const getUploadNotice = (results) => {
  const successCount = results.filter((item) => item.success).length;
  const convertedCount = results.filter((item) => item.converted).length;

  let alertType;
  let messages;
  if (successCount == results.length) {
    alertType = "success";
    messages = [i18n.t("js.components.attachment_dropzone.success_notice")];
  } else if (successCount > 0) {
    alertType = "info";
    messages = [
      i18n.t("js.components.attachment_dropzone.success_message", {
        successful: successCount,
        total: results.length,
      }),
    ];
  } else {
    alertType = "danger";
    messages = [];
  }

  if (convertedCount > 0) {
    messages.push(
      i18n.t("js.components.attachment_dropzone.heic_converted_notice")
    );
  }

  messages = messages.concat(
    results
      .filter((item) => !item.success)
      .map((item) => `${item.file}: ${item.errors}`)
  );

  return { alertType, messages };
};

const getDropzoneText = (uploadState, maxSize) => {
  if (!uploadState.uploading) {
    return i18n.t("js.components.attachment_dropzone.dropzone_text", {
      max: bytesToMB(maxSize),
    });
  }

  if (uploadState.total == 1) {
    return i18n.t("js.components.attachment_dropzone.uploading");
  }

  return i18n.t("js.components.attachment_dropzone.upload_count", {
    completed: uploadState.completed,
    total: uploadState.total,
  });
};

const bytesToMB = (bytes) => Math.round(bytes / 1000 / 1000);

const getAllowedExtensionsText = (extensions) => {
  if (!extensions) {
    return;
  }

  return (
    <p>
      <small>
        {i18n.t("js.components.attachment_dropzone.allowed_extensions_text", {
          extensions: extensions,
        })}
      </small>
    </p>
  );
};

const dispatchStandAloneUploadFailedEvent = (rejectedFileNotice) => {
  // Trigger custom event to notify Stimulus controller
  const event = new CustomEvent("upload:failed", {
    bubbles: true,
    detail: {
      notice: rejectedFileNotice,
    },
  });
  document.dispatchEvent(event);
};

const dispatchStandAloneUploadCompleteEvent = (uploadNotice) => {
  const event = new CustomEvent("upload:complete", {
    bubbles: true,
    detail: {
      notice: uploadNotice,
    },
  });
  document.dispatchEvent(event);
};

const validateFiles = (acceptedFiles, rejectedFiles, MAX_FILES, MAX_SIZE) => {
  if (acceptedFiles.length + rejectedFiles.length > MAX_FILES) {
    const tooManyFilesNotice = {
      alertType: "danger",
      messages: [
        i18n.t("js.components.attachment_dropzone.too_many_files_notice", {
          max_files: MAX_FILES,
        }),
      ],
    };
    return { notice: tooManyFilesNotice, valid: false };
  }

  // Reject if sum of the uploaded files exceed max size
  const totalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
  if (totalSize > MAX_SIZE) {
    const maxSizeExceededNotice = {
      alertType: "danger",
      messages: [
        i18n.t("js.components.attachment_dropzone.rejected_file_notice", {
          max_size: bytesToMB(MAX_SIZE),
        }),
      ],
    };
    return { notice: maxSizeExceededNotice, valid: false };
  }

  if (rejectedFiles.length > 0) {
    for (const file of rejectedFiles) {
      if (file.size > MAX_SIZE) {
        return {
          notice: {
            alertType: "danger",
            messages: [
              i18n.t("js.components.attachment_dropzone.rejected_file_notice", {
                max_size: bytesToMB(MAX_SIZE),
              }),
            ],
          },
          valid: false,
        };
      }
    }

    return {
      notice: {
        alertType: "danger",
        messages: [
          i18n.t("js.components.attachment_dropzone.invalid_file_notice"),
        ],
      },
      valid: false,
    };
  }

  return { valid: true };
};

export default AttachmentDropzone;
export { getUploadNotice };

AttachmentDropzone.propTypes = {
  maxSize: number.isRequired,
  resourceId: oneOfType([string, number]).isRequired,
  resourceType: string.isRequired,
  onUploadComplete: func,
  allowedExtensions: string,
  standAlone: bool,
  context: string,
};
